/**
 * Review + evidence on `shape.meta.review` (not entity-card schema).
 *
 * Shape:
 * {
 *   status: 'pending' | 'confirmed',
 *   origin: 'machine' | 'human',
 *   lastMachineOp: 'add' | 'update' | null,
 *   citations: [{ reportId, title, excerpt, segmentId? }],
 * }
 *
 * Omit meta / missing review = treated as confirmed human.
 */

const CONFIRMED_HUMAN = Object.freeze({
    status: 'confirmed',
    origin: 'human',
    lastMachineOp: null,
    citations: Object.freeze([]),
});

function normalizeCitations(citations) {
    if (!Array.isArray(citations)) {
        return [];
    }
    const out = [];
    for (const item of citations) {
        if (!item || typeof item !== 'object') {
            continue;
        }
        const row = {
            reportId: item.reportId == null ? '' : String(item.reportId),
            title: item.title == null ? '' : String(item.title),
            excerpt: item.excerpt == null ? '' : String(item.excerpt),
        };
        if (item.segmentId != null && item.segmentId !== '') {
            row.segmentId = item.segmentId;
        }
        out.push(row);
    }
    return out;
}

function normalizeReview(review) {
    if (!review || typeof review !== 'object') {
        return {
            status: CONFIRMED_HUMAN.status,
            origin: CONFIRMED_HUMAN.origin,
            lastMachineOp: CONFIRMED_HUMAN.lastMachineOp,
            citations: [],
        };
    }
    return {
        status: review.status === 'pending' ? 'pending' : 'confirmed',
        origin: review.origin === 'machine' ? 'machine' : 'human',
        lastMachineOp: review.lastMachineOp === 'add' || review.lastMachineOp === 'update'
            ? review.lastMachineOp
            : null,
        citations: normalizeCitations(review.citations),
    };
}

/**
 * Reads review from a tldraw shape. Missing `meta.review` is confirmed human.
 *
 * @param {{meta?: {review?: object}}|null|undefined} shape
 */
export function getReview(shape) {
    return normalizeReview(shape?.meta?.review);
}

/**
 * Resolves a review object from a review, `meta`, or shape.
 *
 * @param {object|null|undefined} existingMeta
 */
function resolveReview(existingMeta) {
    if (!existingMeta || typeof existingMeta !== 'object') {
        return normalizeReview(null);
    }
    if (existingMeta.meta && typeof existingMeta.meta === 'object') {
        return normalizeReview(existingMeta.meta.review);
    }
    if (existingMeta.review && typeof existingMeta.review === 'object') {
        return normalizeReview(existingMeta.review);
    }
    return normalizeReview(existingMeta);
}

/**
 * Machine `add_*` → pending + origin machine.
 *
 * @param {{citations?: Array<object>}} [opts]
 */
export function pendingOnAdd({citations} = {}) {
    return {
        status: 'pending',
        origin: 'machine',
        lastMachineOp: 'add',
        citations: normalizeCitations(citations),
    };
}

/**
 * Machine `update_*` on a human-origin or already-confirmed item → pending again.
 * Keeps origin for history (inverse case stays `human`).
 *
 * @param {object|null|undefined} existingMeta review, `shape.meta`, or shape
 */
export function pendingOnUpdate(existingMeta) {
    const existing = resolveReview(existingMeta);
    return {
        status: 'pending',
        origin: existing.origin,
        lastMachineOp: 'update',
        citations: existing.citations,
    };
}

/**
 * Analyst confirm: flip status only; keep origin (and citations / lastMachineOp).
 *
 * @param {object|null|undefined} existingMeta review, `shape.meta`, or shape
 */
export function confirm(existingMeta) {
    const existing = resolveReview(existingMeta);
    return {
        ...existing,
        status: 'confirmed',
    };
}

/**
 * Attach / replace citations on an existing review payload.
 *
 * @param {object|null|undefined} meta review, `shape.meta`, or shape
 * @param {Array<object>} citations
 */
export function withCitations(meta, citations) {
    const existing = resolveReview(meta);
    return {
        ...existing,
        citations: normalizeCitations(citations),
    };
}

/**
 * @param {object|null|undefined} source review, `shape.meta`, or shape
 * @returns {boolean}
 */
export function isReviewPending(source) {
    return resolveReview(source).status === 'pending';
}

/**
 * Legacy pending arrow look (dashed + diamond). No longer applied — the robot
 * icon is the pending cue. Kept so confirm can normalize leftover dashed arrows.
 */
export const PENDING_ARROW_PROPS = Object.freeze({
    dash: 'dashed',
    arrowheadEnd: 'diamond',
});

/** Solid + arrow = human, confirmed, or pending (robot is the pending cue). */
export const CONFIRMED_ARROW_PROPS = Object.freeze({
    dash: 'solid',
    arrowheadEnd: 'arrow',
});

/**
 * Machine add: pending review, copying any plan citations.
 *
 * @param {{citations?: Array<object>}|null|undefined} mutation
 */
export function reviewForMachineAdd(mutation) {
    return pendingOnAdd({citations: mutation?.citations});
}

/**
 * Machine update: pending again even if the item was human-confirmed.
 * Copies new citations when the mutation carries them.
 *
 * @param {object|null|undefined} existingMeta review, `shape.meta`, or shape
 * @param {{citations?: Array<object>}|null|undefined} [mutation]
 */
export function reviewForMachineUpdate(existingMeta, mutation) {
    const next = pendingOnUpdate(existingMeta);
    return mutation?.citations != null ? withCitations(next, mutation.citations) : next;
}

/**
 * Merge `review` onto existing shape.meta without dropping other meta keys.
 *
 * @param {object|null|undefined} existingMeta
 * @param {object} review
 */
export function mergeReviewMeta(existingMeta, review) {
    const base = existingMeta && typeof existingMeta === 'object' ? existingMeta : {};
    return {
        ...base,
        review,
    };
}

/**
 * Write review onto a shape. Cards: meta only. Arrows: optionally normalize dash.
 *
 * @param {object|null|undefined} editor
 * @param {object|null|undefined} shape
 * @param {object} review
 * @param {{arrowVisual?: {dash: string, arrowheadEnd: string}}} [options]
 */
export function writeShapeReview(editor, shape, review, options = {}) {
    if (!editor || !shape || !review) return;
    const patch = {
        id: shape.id,
        type: shape.type,
        meta: mergeReviewMeta(shape.meta, review),
    };
    if (shape.type === 'arrow' && options.arrowVisual) {
        patch.props = {
            ...(shape.props || {}),
            ...options.arrowVisual,
        };
    }
    editor.updateShapes?.([patch]);
}

/**
 * Stamp pending review after a machine add (cards + arrows).
 * Arrows stay solid; the robot icon is the pending cue.
 *
 * @param {object|null|undefined} editor
 * @param {string|null|undefined} shapeId
 * @param {{citations?: Array<object>}|null|undefined} [mutation]
 */
export function applyPendingAddToShape(editor, shapeId, mutation) {
    const shape = editor?.getShape?.(shapeId);
    if (!shape) return;
    writeShapeReview(editor, shape, reviewForMachineAdd(mutation), {
        arrowVisual: CONFIRMED_ARROW_PROPS,
    });
}

/**
 * Analyst confirm: flip `meta.review.status` only. Arrows also restore solid + arrow.
 *
 * @param {object|null|undefined} editor
 * @param {object|null|undefined} shape
 */
export function confirmShapeReview(editor, shape) {
    if (!editor || !shape) return;
    writeShapeReview(editor, shape, confirm(shape), {
        arrowVisual: CONFIRMED_ARROW_PROPS,
    });
}

/**
 * Reject a machine change.
 *
 * - lastMachineOp `add`: delete only this shape (card or arrow). Does not wipe
 *   the rest of the chart.
 * - lastMachineOp `update`: prior geometry/label is not stored on meta, so we
 *   cannot revert. Keep the shape and confirm review (robot disappears). Safer
 *   than deleting a human-created item the machine only edited.
 *
 * @param {object|null|undefined} editor
 * @param {object|null|undefined} shape
 * @returns {'deleted'|'confirmed'|null}
 */
export function rejectShapeReview(editor, shape) {
    if (!editor || !shape) return null;
    const review = getReview(shape);
    if (review.lastMachineOp === 'add') {
        editor.deleteShapes?.([shape.id]);
        return 'deleted';
    }
    confirmShapeReview(editor, shape);
    return 'confirmed';
}
